import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getAuditTrailByid, getAuditTrailByPreview } from '../../../../redux/auditTrailSlice';
import { getAllVersionHistories } from '../../../../redux/versionHistorySlice';
import { useNavigate, useParams } from 'react-router-dom';
import ProcedureStatusInfo from './ProcedureStatusInfo';
import PreviewContent from '../../../AuditTrail/ActionComponents/components/PreviewContent';
import Loader from '../../../../common/Loader';
import { processContentData } from '../../../AuditTrail/ActionComponents/utils/previewUtils';
import VersionHistoryPopup from "../../../../common/VersionHistoryPopup/VersionHistoryPopup";
import AppBar from '../../../../common/AppBar';

/**
 * VersionHistoryPreview - Shows version history preview for documents
 * Based on RevisionPreview from Audit Trail
 */
const VersionHistoryPreview = () => {
  const [selectedView, setSelectedView] = useState("new");

  const [showPreview, setShowPreview] = useState(false);

  const dispatch = useDispatch()
  const { audit_trail_id } = useParams()
  const navigate = useNavigate()
  const [audit_trail_data, setAuditData] = useState({})
  const [originalContent, setOriginalContent] = useState({ data: [], loaded: false });
  const [newContent, setNewContent] = useState({ data: [], loaded: false });
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState({});

  const { loading } = useSelector(state => state.audittrail)

  const { versionHistories, loading: isLoadingVersions } = useSelector(
    (state) => state.versionHistory
  );

  useEffect(() => {
    (async () => {
      try {
        const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }))
        const data = resp.payload?.results || resp.payload?.data?.results || resp.payload;
        setAuditData(data);
        initializeSelectedNodes(data);
      } catch (error) {
        console.error('Error fetching audit trail:', error);
      }
    })()
  }, [dispatch, audit_trail_id])

  const initializeSelectedNodes = (data) => {
    const selected = {};
    const versionContent = data.version_content;

    // Mark all relevant nodes as selected (checked)
    if (versionContent) {
      // Mark section if available
      if (versionContent.section_tree_node_id) {
        selected[versionContent.section_tree_node_id] = true;
      }

      // Mark step if available
      if (versionContent.step_tree_node_id) {
        selected[versionContent.step_tree_node_id] = true;
      }

      // Mark by pageTitle for step matching
      if (versionContent.pageTitle) {
        selected[versionContent.pageTitle] = true;
      }

      // Mark child node by element_id or specific tree_node_id
      if (versionContent.element_id) {
        selected[versionContent.element_id] = true;
      }
      if (versionContent.video_tree_node_id) {
        selected[versionContent.video_tree_node_id] = true;
      }
      if (versionContent.rich_text_tree_node_id) {
        selected[versionContent.rich_text_tree_node_id] = true;
      }
      if (versionContent.canvas_tree_node_id) {
        selected[versionContent.canvas_tree_node_id] = true;
      }
      if (versionContent.attachment_tree_node_id) {
        selected[versionContent.attachment_tree_node_id] = true;
      }
    }

    setSelectedNodes(selected);
  };

  // Disable node toggling - checkboxes should be checked and disabled
  const handleNodeToggle = (nodeId) => {
    // Do nothing - checkboxes are disabled
  };

  useEffect(() => {
    (async () => {
      if (audit_trail_data && (audit_trail_data.version_content || audit_trail_data.steps || Array.isArray(audit_trail_data))) {
        // Check both main version_content and parent_version_data for newContent/originingalContent
        const mainVersionContent = audit_trail_data.version_content;
        const parentVersionContent = audit_trail_data.parent_version_data?.version_content;

        // Try to get content from main version_content first, then parent_version_data
        const contentToSend = mainVersionContent
          ? (selectedView === "new"
            ? (mainVersionContent.newContent || parentVersionContent?.newContent)
            : (mainVersionContent.originingalContent || parentVersionContent?.originingalContent))
          : (parentVersionContent
            ? (selectedView === "new"
              ? parentVersionContent.newContent
              : parentVersionContent.originingalContent)
            : null);

        // Get move_copy_version_id_ref from either main or parent
        const moveCopyVersionIdRef = mainVersionContent?.move_copy_version_id_ref || parentVersionContent?.move_copy_version_id_ref || "";
        const createdAt = audit_trail_data.version_created_at || "";

        const needsFetch = selectedView === "new" ? !newContent.loaded : !originalContent.loaded;
        const hasValidContent = Array.isArray(contentToSend) && contentToSend.length > 0;

        // Check if audit_trail_data has direct steps structure (for non-move/copy operations)
        const hasDirectStepsStructure = Array.isArray(audit_trail_data.steps) && audit_trail_data.steps.length > 0;
        const hasDirectSectionsStructure = Array.isArray(audit_trail_data) && audit_trail_data.length > 0 && audit_trail_data[0]?.steps;

        if (needsFetch && hasValidContent) {
          // Handle move/copy operations with newContent/originingalContent
          try {
            const resp = await dispatch(getAuditTrailByPreview({
              move_copy_version_id_ref: moveCopyVersionIdRef,
              created_at: createdAt,
              data: contentToSend
            }))

            const resultData = resp.payload?.result || resp.payload?.data?.result || [];
            if (selectedView === "new") {
              setNewContent({ data: resultData, loaded: true });
            } else {
              setOriginalContent({ data: resultData, loaded: true });
            }
          } catch (error) {
            console.error('VersionHistoryPreview - Error fetching preview:', error);
            if (selectedView === "new") {
              setNewContent({ data: [], loaded: true });
            } else {
              setOriginalContent({ data: [], loaded: true });
            }
          }
        } else if (needsFetch && !hasValidContent) {
          // Handle cases where there's no newContent/originingalContent
          // For step updates, construct step structure from version_content
          let directData = [];

          if (hasDirectSectionsStructure) {
            // If audit_trail_data is an array of sections
            directData = audit_trail_data;
          } else if (hasDirectStepsStructure) {
            // If audit_trail_data has steps directly
            directData = [{
              ...audit_trail_data,
              steps: audit_trail_data.steps
            }];
          } else if (mainVersionContent?.allRevisionNodes) {
            // Try allRevisionNodes if available
            directData = mainVersionContent.allRevisionNodes;
          } else if (mainVersionContent && mainVersionContent.type === "step") {
            // For step updates, construct a section with the step
            // This creates a structure that PreviewContent can render
            const stepData = {
              version_details: {
                version_id: audit_trail_data.version_id,
                version_content: {
                  ...mainVersionContent,
                  pageTitle: mainVersionContent.pageTitle || mainVersionContent.stepTitle || "Step",
                  stepTitle: mainVersionContent.pageTitle || mainVersionContent.stepTitle || "Step"
                }
              },
              step_tree_node_id: mainVersionContent.step_tree_node_id,
              short_description: mainVersionContent.short_description,
              sibling_order: mainVersionContent.sibling_order,
              children: [], // Empty children array
              substeps: [] // Empty substeps array
            };

            const sectionData = {
              version_details: {
                version_id: audit_trail_data.version_id,
                version_content: {
                  section: mainVersionContent.section,
                  section_tree_node_id: mainVersionContent.section_tree_node_id,
                  section_name: mainVersionContent.section
                }
              },
              section_tree_node_id: mainVersionContent.section_tree_node_id,
              section_name: mainVersionContent.section,
              steps: [stepData]
            };

            directData = [sectionData];
          } else {
          }

          if (selectedView === "new") {
            setNewContent({ data: directData, loaded: true });
          } else {
            setOriginalContent({ data: directData, loaded: true });
          }
        }
      } else if (audit_trail_data && Object.keys(audit_trail_data).length > 0) {
        // If we have audit_trail_data but no version_content, mark as loaded with empty data
        if (selectedView === "new" && !newContent.loaded) {
          setNewContent({ data: [], loaded: true });
        } else if (selectedView === "original" && !originalContent.loaded) {
          setOriginalContent({ data: [], loaded: true });
        }
      }
    })()
  }, [dispatch, audit_trail_data, selectedView, newContent.loaded, originalContent.loaded])

  const rawContentData = selectedView === "new" ? newContent?.data : originalContent?.data;
  const isContentLoaded = selectedView === "new" ? newContent.loaded : originalContent.loaded;

  const processedData = processContentData(rawContentData);

  // Update selectedNodes to mark all nodes as checked when content is loaded
  useEffect(() => {
    if (isContentLoaded && processedData.length > 0) {
      const allSelected = {};

      // Mark all sections, steps, and child nodes as selected
      processedData.forEach(section => {
        // Mark section
        const sectionId = section.version_details?.version_content?.section_tree_node_id;
        if (sectionId) {
          allSelected[sectionId] = true;
        }

        // Mark steps and their children
        section.steps?.forEach(step => {
          const stepId = step.version_details?.version_content?.step_tree_node_id;
          const stepPageTitle = step.version_details?.version_content?.pageTitle;
          if (stepId) {
            allSelected[stepId] = true;
          }
          if (stepPageTitle) {
            allSelected[stepPageTitle] = true;
          }

          // Mark step children
          step.children?.forEach(child => {
            const childId = child.version_details?.version_content?.element_id ||
              child.version_details?.version_content?.video_tree_node_id ||
              child.version_details?.version_content?.rich_text_tree_node_id ||
              child.version_details?.version_content?.canvas_tree_node_id ||
              child.version_details?.version_content?.attachment_tree_node_id;
            if (childId) {
              allSelected[childId] = true;
            }
          });

          // Mark substeps and their children
          step.substeps?.forEach(substep => {
            const substepId = substep.version_details?.version_content?.sub_step_tree_node_id;
            if (substepId) {
              allSelected[substepId] = true;
            }

            substep.children?.forEach(child => {
              const childId = child.version_details?.version_content?.element_id ||
                child.version_details?.version_content?.video_tree_node_id ||
                child.version_details?.version_content?.rich_text_tree_node_id ||
                child.version_details?.version_content?.canvas_tree_node_id ||
                child.version_details?.version_content?.attachment_tree_node_id;
              if (childId) {
                allSelected[childId] = true;
              }
            });
          });
        });
      });

      setSelectedNodes(allSelected);
    }
  }, [isContentLoaded, processedData.length, selectedView]);

  const targetPageTitle = audit_trail_data.version_content?.pageTitle;
  const targetElementId = audit_trail_data.version_content?.element_id;
  const targetStepTreeNodeId = audit_trail_data.version_content?.step_tree_node_id;
  const targetVersionIdRef = audit_trail_data.version_content?.version_id_ref;
  const targetVideoTreeNodeId = audit_trail_data.version_content?.video_tree_node_id;
  const targetRichTextTreeNodeId = audit_trail_data.version_content?.rich_text_tree_node_id;
  const targetCanvasTreeNodeId = audit_trail_data.version_content?.canvas_tree_node_id;
  const targetAttachmentTreeNodeId = audit_trail_data.version_content?.attachment_tree_node_id;
  const versionContentType = audit_trail_data.version_content?.type;

  // Filter content by pageTitle if available, otherwise show all content
  // If no targetPageTitle, show all processed data
  // For constructed step structures (type === "step"), skip filtering as we already have the correct step
  let contentData = processedData;

  // Skip filtering if this is a constructed step structure (we built it specifically for this step)
  const isConstructedStepStructure = versionContentType === "step" &&
    processedData.length === 1 &&
    processedData[0]?.steps?.length === 1 &&
    processedData[0].steps[0].version_details?.version_content?.type === "step";

  if (targetPageTitle && processedData.length > 0 && !isConstructedStepStructure) {
    // Try to filter by pageTitle
    contentData = processedData.map(section => {
      // Check if any step matches
      const matchingSteps = section.steps?.filter(step => {
        const stepPageTitle = step.version_details?.version_content?.pageTitle;
        return stepPageTitle === targetPageTitle;
      }) || [];

      // Check children in steps - include steps that have matching children
      const stepsWithMatchingChildren = section.steps?.map(step => {
       
        const matchingChildren = step.children?.filter(child => {
          const childPageTitle = child.version_details?.version_content?.pageTitle;
          const childElementId = child.version_details?.version_content?.element_id;
          const childVersionId = child.version_details?.version_id;
          const childVersionIdRef = child.version_details?.version_content?.version_id_ref;
          const childVideoTreeNodeId = child.version_details?.version_content?.video_tree_node_id;
          const childRichTextTreeNodeId = child.version_details?.version_content?.rich_text_tree_node_id;
          const childCanvasTreeNodeId = child.version_details?.version_content?.canvas_tree_node_id;
          const childAttachmentTreeNodeId = child.version_details?.version_content?.attachment_tree_node_id;

          // Match by pageTitle, element_id, version_id (from version_details), version_id_ref, or specific tree_node_id
          return childPageTitle === targetPageTitle ||
            (targetElementId && childElementId === targetElementId) ||
            (targetVersionIdRef && (childVersionId === targetVersionIdRef || childVersionIdRef === targetVersionIdRef)) ||
            (targetVideoTreeNodeId && childVideoTreeNodeId === targetVideoTreeNodeId) ||
            (targetRichTextTreeNodeId && childRichTextTreeNodeId === targetRichTextTreeNodeId) ||
            (targetCanvasTreeNodeId && childCanvasTreeNodeId === targetCanvasTreeNodeId) ||
            (targetAttachmentTreeNodeId && childAttachmentTreeNodeId === targetAttachmentTreeNodeId);
        }) || [];

        // Check substeps
        const matchingSubsteps = step.substeps?.map(substep => {
          const matchingSubstepChildren = substep.children?.filter(child => {
            const childPageTitle = child.version_details?.version_content?.pageTitle;
            const childElementId = child.version_details?.version_content?.element_id;
            const childVersionId = child.version_details?.version_id;
            const childVersionIdRef = child.version_details?.version_content?.version_id_ref;
            const childVideoTreeNodeId = child.version_details?.version_content?.video_tree_node_id;
            const childRichTextTreeNodeId = child.version_details?.version_content?.rich_text_tree_node_id;
            const childCanvasTreeNodeId = child.version_details?.version_content?.canvas_tree_node_id;
            const childAttachmentTreeNodeId = child.version_details?.version_content?.attachment_tree_node_id;

            // Match by pageTitle, element_id, version_id (from version_details), version_id_ref, or specific tree_node_id
            return childPageTitle === targetPageTitle ||
              (targetElementId && childElementId === targetElementId) ||
              (targetVersionIdRef && (childVersionId === targetVersionIdRef || childVersionIdRef === targetVersionIdRef)) ||
              (targetVideoTreeNodeId && childVideoTreeNodeId === targetVideoTreeNodeId) ||
              (targetRichTextTreeNodeId && childRichTextTreeNodeId === targetRichTextTreeNodeId) ||
              (targetCanvasTreeNodeId && childCanvasTreeNodeId === targetCanvasTreeNodeId) ||
              (targetAttachmentTreeNodeId && childAttachmentTreeNodeId === targetAttachmentTreeNodeId);
          }) || [];

          return matchingSubstepChildren.length > 0 ? {
            ...substep,
            children: matchingSubstepChildren
          } : null;
        }).filter(Boolean) || [];

        if (matchingChildren.length > 0 || matchingSubsteps.length > 0) {
          return {
            ...step,
            children: matchingChildren.length > 0 ? matchingChildren : step.children,
            substeps: matchingSubsteps.length > 0 ? matchingSubsteps : step.substeps
          };
        }

        return null;
      }).filter(Boolean) || [];

      if (matchingSteps.length > 0) {
        return {
          ...section,
          steps: matchingSteps,
          hideSectionHeader: true
        };
      } else if (stepsWithMatchingChildren.length > 0) {
        return {
          ...section,
          steps: stepsWithMatchingChildren,
          hideSectionHeader: true
        };
      }

      return null;
    }).filter(Boolean);

    // If filtering resulted in empty array, show all content instead
    if (contentData.length === 0) {
      contentData = processedData;
    }
  }

  const displayTitle = audit_trail_data.version_id
    ? `${audit_trail_data.version_content?.pageTitle ||
    audit_trail_data.version_content?.elementTitle ||
    audit_trail_data.version_content?.stepTitle ||
    audit_trail_data.version_content?.section ||
    audit_trail_data.version_content?.partNumber ||
    "Item"
    } (Version ${audit_trail_data.version_id}) Selected`
    : "Loading..."

  const handleReviewAgainstDifferentVersion = async () => {
    await dispatch(getAllVersionHistories({ versionId: audit_trail_data.version_id }));
    setShowVersionHistory(true);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleCopy = () => {
    // TODO: Implement copy functionality
  };

  return <div style={{ paddingBottom: "10px" }}>

    {!showPreview ? (
      // Initial view with Cancel, Tables, Review, Copy buttons
      <AppBar
        title={displayTitle}
        showTablesDropdown={true}
        showCancelButton={true}
        showBackButton={false}
        showProfileButton={false}
        buttons={[
          {
            label: "Review",
            onClick: handlePreview,
          },
          //   {
          //     label: isLoadingVersions ? "Loading..." : "Review",
          //     onClick: handleReviewAgainstDifferentVersion,
          //   },
          {
            label: "Copy",
            onClick: handleCopy,
          }
        ]}
      />
    ) : (
      // Preview view with View Original/View New radio buttons and Review Against Different Version
      <div>
        <header className="appbar" style={{ display: 'flex', alignItems: 'center', padding: '12px 20px', backgroundColor: '#ffffff', borderBottom: '1px solid #e0e0e0' }}>
          <button
            className="appbar-button-secondary"
            onClick={() => setShowPreview(false)}
            style={{
              backgroundColor: "#f1f3f4",
              color: "#47515c",
              border: "none",
              padding: "8px 12px",
              cursor: "pointer",
              borderRadius: "4px",
              marginLeft: "15px",
              marginRight: "15px",
            }}
          >
            Cancel
          </button>

          {/* Radio Buttons for View Toggle */}
          <div
            className="view-toggle"
            style={{
              width: "300px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginLeft: "20px",
            }}
          >
            <label
              style={{
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="view"
                value="original"
                checked={selectedView === "original"}
                onChange={() => setSelectedView("original")}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{ fontSize: "10px", whiteSpace: "nowrap", marginTop: "3px" }}
              >
                View Original
              </span>
            </label>
            <label
              style={{
                fontSize: "15px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="view"
                value="new"
                checked={selectedView === "new"}
                onChange={() => setSelectedView("new")}
                style={{ cursor: "pointer" }}
              />
              <span
                style={{ fontSize: "10px", whiteSpace: "nowrap", marginTop: "3px" }}
              >
                View New
              </span>
            </label>
          </div>

          <h2 style={{ fontSize: "20px", color: "#0D6EFD", marginLeft: "20px", flex: 1 }}>
            {displayTitle}
          </h2>

          <div className="appbar-buttons" style={{ display: 'flex', gap: '10px' }}>
            <button
              className="appbar-button-secondary"
              onClick={handleReviewAgainstDifferentVersion}
              disabled={isLoadingVersions}
            >
              {isLoadingVersions ? "Loading..." : "Review Against Different Version"}
            </button>
          </div>
        </header>
      </div>
    )}

    {loading ? <Loader /> : <>
      <ProcedureStatusInfo
        item_code_type={audit_trail_data.version_content?.item_code_type}
        partNumber={audit_trail_data.version_content?.partNumber}
        status={audit_trail_data.version_content?.status}
        revision={audit_trail_data.version_content?.rev}
        partDescription={audit_trail_data.version_content?.partDescription}
      />

      {!isContentLoaded ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div>Loading...</div>
        </div>
      ) : contentData && contentData.length > 0 ? (
        <div style={{ padding: '20px' }}>
          <PreviewContent
            contentData={contentData}
            showCheckboxes={true}
            selectedNodes={selectedNodes}
            onNodeToggle={handleNodeToggle}
            disabled={true}
          />
        </div>
      ) : (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <div>No content to display</div>
        </div>
      )}
    </>}

    {showVersionHistory && (
      <VersionHistoryPopup
        onClose={() => setShowVersionHistory(false)}
        versionHistories={versionHistories || []}
        mode="edit"
        versionId={audit_trail_data.version_id}
        nodeId={audit_trail_data.version_content?.element_id || audit_trail_data.version_content?.step_tree_node_id}
        nodeIdField="element_id"
        breadcrumbText={audit_trail_data.version_content?.breadcrumb_text}
        comparisonSelect
        initialFilterType="all"
        currentVersionId={audit_trail_data.version_id}
        onViewDetails={(selectedVersion) => {
          setShowVersionHistory(false);
          navigate(`/audit-trail/move-preview/${audit_trail_id}?compareWith=${selectedVersion.version_id}`);
        }}
      />
    )}
  </div>
}

export default VersionHistoryPreview
